import { useHotkeys } from '@shelf/hotkeys';
import { useParamsSafe } from '@/hooks/useParamsSafe';
import { useCallback, useState } from 'react';
import { useStageScreenViewport } from './useStageScreenViewport';
import { useStageStyles } from './useStageStyles';
import { StageActiveEngagement } from '@/engagements/StageActiveEngagement';
import { StageElement } from './StageElement';
import { StageChrome } from './StageChrome';
import { useStageEvent } from '@/hooks/useStageEvent';
import { useSearchParams } from 'react-router-dom';
import { useGoogleFonts } from '@/hooks/useGoogleFonts';
import { ScreenThemeStateProvider } from '@/providers/ScreenThemeStateProvider';
import { EventScreenThemeProvider } from '@/providers/ScreenThemeStateProvider/EventScreenThemeProvider';
import { useScreenTheme } from '@/providers/ScreenThemeStateProvider/EventScreenThemeContext';
import { FullScreenLoader } from '@/components/Loader';
import { ErrorScreen } from '@/components/ErrorScreen';
import {
  deleteScreenElement,
  selectScreenElement,
} from '@/providers/sharedActions';
import { EngagementMode } from '@/types/screen';
import { StageGuideEngagement } from './StageGuideEngagement';
import { useAuth } from '@/hooks/useAuth';
import { Role } from '@/generated/graphql';
import { StageFreeformPresentationProvider } from '@/engagements/slides/StageFreeformPresentation';
import { useStageFreeformPresentation } from '@/engagements/slides/stageFreeformPresentationContext';

export function EventStageScreen() {
  const { id } = useParamsSafe('id');
  const { user } = useAuth();

  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get('preview') === 'true';

  useStageScreenViewport();

  const { data, loading, error } = useStageEvent(id);

  const [engagementMode, setEngagementMode] = useState<EngagementMode>(
    isPreview ? EngagementMode.None : EngagementMode.Actual,
  );

  if (!data?.event) {
    if (loading) {
      return <FullScreenLoader />;
    }
    return <ErrorScreen error={error} />;
  } else if (!data.event.live && (!user || user.role === Role.Anon)) {
    return <ErrorScreen error={new Error('Event is not live')} />;
  }

  return (
    <ScreenThemeStateProvider
      event={data.event}
      screenType="stage"
      engagementMode={engagementMode}
      setEngagementMode={setEngagementMode}
    >
      <EventScreenThemeProvider>
        <StageFreeformPresentationProvider>
          <Screen />
        </StageFreeformPresentationProvider>
      </EventScreenThemeProvider>
    </ScreenThemeStateProvider>
  );
}

const DEFAULT_NULL_FAMILY: string[] = [];

function Screen() {
  const [searchParams] = useSearchParams();
  const editor =
    searchParams.get('editor') === 'true' &&
    searchParams.get('preview') !== 'true';
  const {
    dispatch,
    state: { savedTheme, draftTheme, selectedElementId },
    state,
  } = useScreenTheme();
  const { isFreeformSlide } = useStageFreeformPresentation();
  const hideThemeStageElements = isFreeformSlide;

  const { rootStyles } = useStageStyles({ state });

  useGoogleFonts({
    fontFamily:
      draftTheme?.headingFontFamily ||
      savedTheme?.headingFontFamily ||
      DEFAULT_NULL_FAMILY,
  });

  useGoogleFonts({
    fontFamily:
      draftTheme?.bodyFontFamily ||
      savedTheme?.bodyFontFamily ||
      DEFAULT_NULL_FAMILY,
  });

  useHotkeys({
    Backspace: useCallback(() => {
      if (editor && selectedElementId) {
        dispatch(deleteScreenElement({ id: selectedElementId }));
      }
    }, [editor, selectedElementId, dispatch]),
  });

  const handleDeselectAll = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const dataName = (e.target as HTMLElement).getAttribute('data-name');
      if (editor && !dataName?.startsWith('BOUNDING_BOX')) {
        dispatch(selectScreenElement({ id: undefined }));
      }
    },
    [editor, dispatch],
  );

  let children: React.ReactNode;
  const stageElements = hideThemeStageElements
    ? []
    : Object.keys(savedTheme.stageElements).map((elementId) => (
        <StageElement key={elementId} elementId={elementId} editor={editor} />
      ));

  if (editor) {
    children = (
      <>
        {/* in editor, show engagement below elements */}
        {state.engagementMode === EngagementMode.Actual &&
          state.activeEngagement && <StageActiveEngagement />}
        {state.engagementMode === EngagementMode.Guide && (
          <StageGuideEngagement />
        )}
        {stageElements}
      </>
    );
  } else {
    children = (
      <>
        {stageElements}
        {/* in non-editor, show actual engagement above elements */}
        {state.activeEngagement && <StageActiveEngagement />}
      </>
    );
  }

  return (
    <div
      data-name="STAGE_ROOT"
      className={`relative flex h-screen w-screen flex-col gap-4 overflow-hidden`}
      style={rootStyles}
      onClick={handleDeselectAll}
    >
      <StageChrome name="STAGE_CHROME">{children}</StageChrome>
    </div>
  );
}
